// Libraries
import { visuallyHidden } from '@mui/utils'
import { West, East } from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Breadcrumbs, Collapse, Fade, IconButton, Link, Typography } from '@mui/material'

// Components
import TabPageForApproval from '../components/timesheet-filters/TabPageForApproval'
import ApprovalDetailsPage from '../components/timesheet/ApprovalDetailsPage'
import MassApprovalFeedback from '../components/timesheet/details/v2/MassApprovalFeedback'

// API
import { timesheetApi } from '../store/api/timesheet/timesheetApi'
import { driversApi } from '../store/api/timesheet/timesheetDriversApi'
import { availabilityApi } from '../store/api/timesheet/availabilityApi'
import { branchesApi } from '../store/api/timesheet/timesheetBranchesApi'
import { workTypesApi } from '../store/api/timesheet/timesheetWorkTypesApi'
import { timesheetDetailApi } from '../store/api/timesheet/timesheetDetailApi'
import { timesheetMessageTemplatesApi } from '../store/api/timesheet/timesheetMessageTemplatesApi'
import { useGetTimesheetQuery } from '../utils/hooks/useGetTimesheetQuery'
import { benefitCodesApi } from '../store/api/timesheet/timesheetBenefitCodesApi'
import {
	timesheetCompanyGratificationApi,
	timesheetCompanyGratificationParamsApi,
} from '../store/api/timesheet/timesheetCompanyGratificationApi'

// Redux Slices
import { changeLoading } from '../store/slices/function/timesheetLoaderSlice'
import { addDetailsDriver } from '../store/slices/timesheet/timesheetPageDetailSlice'
import { hasChanges } from '../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalHasChangesSlice'
import { approvalChange } from '../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsFeedbackSlice'
import TableMotoristasFiltersService from '../components/timesheet-filters/TableMotoristasFiltersService'

// Hooks/Utils
import { useHideInEnvironment } from '../utils/hooks/useHideInEnvironment'

function Timesheet(): React.JSX.Element {
	const isProd = useHideInEnvironment('prod')
	const params = useParams()
	const dispatch = useDispatch()
	const url = window.location.href
	const { data: timesheetData, isFetching: isFetchingTimesheet } = useGetTimesheetQuery()

	const [isGridCollapsed, setIsGridCollapsed] = useState(false)

	/**
	 * Effect to update the loading state in the Redux store based on the fetching status of the timesheet data.
	 * This effect runs whenever the isFetchingTimesheet value changes, which indicates whether the timesheet data is currently being fetched from the API. It dispatches an action to update the loading state in the Redux store, allowing the application to display a loading indicator or perform other actions based on the loading state.
	 * @see useGetTimesheetQuery for fetching timesheet data from the API, which provides the isFetchingTimesheet value used in this effect.
	 */
	useEffect(() => {
		dispatch(changeLoading({ loading: isFetchingTimesheet }))
	}, [isFetchingTimesheet])

	/**
	 * Effect to reset API state when the component unmounts.
	 * This effect returns a cleanup function that dispatches actions to reset the API state for various slices of the Redux store when the component unmounts. This ensures that any cached data or state related to the timesheet details is cleared when the user navigates away from the page, preventing stale data from being displayed if they return later.
	 * @see branchesApi, driversApi, timesheetDetailApi, timesheetApi, availabilityApi, benefitCodesApi, workTypesApi, timesheetCompanyGratificationApi, timesheetCompanyGratificationParamsApi for managing API state in the Redux store, which are all reset in this effect to ensure a clean state when the component unmounts.
	 * @see useGetTimesheetQuery for fetching timesheet data from the API, which may have cached data that needs to be cleared when the component unmounts, and is reset in this effect to ensure that stale data is not displayed if the user returns to the page later.
	 * @see addDetailsDriver for managing driver details in the Redux store, which is also reset in a separate effect to ensure that driver details are cleared when the component unmounts, preventing stale data from being displayed if the user returns to the page later.
	 * @see approvalChange for managing approval state in the Redux store, which is reset when new timesheet data is loaded to ensure that the approval state reflects the current timesheet being viewed.
	 * @see hasChanges for managing the state of unsaved changes in the Redux store, which is also reset when new timesheet data is loaded to ensure that the state of unsaved changes reflects the current timesheet being viewed.
	 * @see changeTab for managing the active tab state in the Redux store, which is reset to the default tab when new timesheet data is loaded to ensure that the user starts on a consistent tab when viewing different timesheets.
	 * @see updateProcessedParam for updating the URL search parameters, which is called when the tab changes to ensure that the URL reflects the current state of the application, and is also called in this effect to reset the URL parameters when new timesheet data is loaded.
	 * @see useSearchParams for managing URL search parameters in React Router, which provides the setSearchParams function used in this effect to reset the URL parameters when new timesheet data is loaded, ensuring that the URL reflects the current state of the application.
	 * @see useParams for accessing URL parameters in React Router, which provides the params used in this effect to determine when new timesheet data is loaded based on changes to the timesheet ID in the URL.
	 */
	useEffect(() => {
		return () => {
			dispatch(branchesApi.util.resetApiState())
			dispatch(driversApi.util.resetApiState())
			dispatch(timesheetDetailApi.util.resetApiState())
			dispatch(timesheetApi.util.resetApiState())
			dispatch(availabilityApi.util.resetApiState())
			dispatch(benefitCodesApi.util.resetApiState())
			dispatch(workTypesApi.util.resetApiState())
			dispatch(timesheetCompanyGratificationApi.util.resetApiState())
			dispatch(timesheetCompanyGratificationParamsApi.util.resetApiState())
			dispatch(timesheetMessageTemplatesApi.util.resetApiState())
		}
	}, [])

	useEffect(() => {
		return () => {
			dispatch(
				addDetailsDriver({
					driverTableId: 0,
					driver: {
						driverName: '',
						timesheetDate: '',
						totalServices: 0,
						timeSheetId: null,
						status: '',
						comments: '',
					},
				}),
			)
		}
	}, [])

	useEffect(() => {
		if (!params.id) {
			return
		} else {
			dispatch(
				addDetailsDriver({
					driverTableId: 0,
					driver: {
						driverName: '',
						timesheetDate: '',
						totalServices: 0,
						timeSheetId: null,
						status: '',
						comments: '',
					},
				}),
			)
		}
	}, [params.id])

	useEffect(() => {
		if (timesheetData && !isFetchingTimesheet) {
			dispatch(
				addDetailsDriver({
					driverTableId: timesheetData.timeSheetId,
					driver: {
						timeSheetId: timesheetData.timeSheetId,
						driverName: timesheetData.driver.driverName,
						timesheetDate: timesheetData.timeSheetDate.split('T')[0],
						totalServices: timesheetData.services.length,
						comments: timesheetData.comments,
						status: timesheetData.status || '',
					},
				}),
			)

			dispatch(
				approvalChange({
					approval: false,
					edit: false,
					noApproval: false,
					error: false,
				}),
			)

			dispatch(hasChanges({ hasChangesFromReviewer: false }))

			window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
		}
	}, [timesheetData, isFetchingTimesheet])

	const toggleGridCollapse = () => {
		setIsGridCollapsed(!isGridCollapsed)
	}

	return (
		<Box
			id='timesheet'
			sx={{
				position: 'relative',
				maxWidth: 'calc(100vw - 110px)',
				display: 'flex',
				flexDirection: 'column',
				overflowY: 'visible',
				overflowX: 'auto',
				flexGrow: 1,
				minHeight: 0,
				gap: '24px',
			}}
		>
			<Box
				sx={{
					minWidth: 'fit-content',
					display: 'flex',
					alignItems: 'center',
					gap: '24px',
				}}
			>
				<Box
					sx={{
						margin: 0,
						padding: 0,
						minWidth: isGridCollapsed ? 'fit-content' : '29rem',
						transition: 'all 0.2s ease-in-out',
					}}
				>
					<Breadcrumbs
						aria-label='breadcrumb'
						sx={{
							width: '100%',
							maxWidth: '29rem',
							display: 'flex',
							alignItems: 'center',
							color: 'var(--primary-light)',
						}}
					>
						<Link
							underline='none'
							href='/'
						>
							<Typography
								sx={{ fontFamily: 'var(--font-family)', fontSize: '0.875rem', color: 'var(--primary-light)' }}
							>
								Home
							</Typography>
						</Link>
						{url.split('/').length > 2 && (
							<Link
								underline='none'
								href={url.split('/').slice(0, -1).join('/')} // navigate to the second last part of the url
							>
								<Typography
									color='var(--primary-light)'
									fontSize='0.875rem'
									sx={{ fontFamily: 'var(--font-family)', fontSize: '0.875rem', color: 'var(--primary-light)' }}
								>
									...
								</Typography>
							</Link>
						)}
						<Typography sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary)' }}>Folhas de Ponto</Typography>
					</Breadcrumbs>
				</Box>

				{/**
				 * ! NOT TESTED, hide in production
				 */}
				{!isProd && <MassApprovalFeedback />}
			</Box>
			<Box
				sx={{
					position: 'relative',
					height: '200px',
					display: 'grid',
					gridTemplateColumns: 'auto 1fr',
					flexGrow: 1,
					minHeight: 0,
					alignItems: 'stretch',
					flexWrap: 'nowrap',
					gap: '24px',
					overflowX: 'visible',
				}}
			>
				<Box
					id='timesheet-filters-drawer'
					sx={{
						overflowX: 'visible',
						display: 'flex',
						flexDirection: 'column',
						minHeight: 0,
						width: isGridCollapsed ? '0px' : '29rem',
						height: '100%',
						backgroundColor: isGridCollapsed ? 'var(--secondary-light)' : '#FFFFFF',
						paddingLeft: '15px',
						paddingRight: '15px',
						paddingBottom: '15px',
						borderRadius: '0.875rem',
						transition: 'all 0.2s ease-in-out',
						position: 'relative',
					}}
				>
					{/* Botão de toggle sempre visível */}
					<IconButton
						size='small'
						onClick={toggleGridCollapse}
						sx={{
							overflow: 'visible',
							position: 'absolute',
							right: '-15px',
							top: '20px',
							backgroundColor: '#FFFFFF',
							border: 'none',
							boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
							width: '36px',
							height: '36px',
							padding: '8px',
							'&:hover': {
								backgroundColor: '#F5F5F5',
								boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
							},
							transition: 'all 0.2s ease-in-out',
							marginRight: !isGridCollapsed ? '0px' : '5px',
							marginTop: '-15px',
							borderRadius: '50%',
						}}
					>
						{isGridCollapsed ? (
							<East sx={{ color: 'var(--primary)', width: '20px', height: '20px' }} />
						) : (
							<West sx={{ color: 'var(--primary)', width: '20px', height: '20px' }} />
						)}
						<span style={visuallyHidden}>{isGridCollapsed ? 'Expand grid' : 'Collapse grid'}</span>
					</IconButton>

					<Collapse
						in={!isGridCollapsed}
						timeout={250}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
							minHeight: 0,
							'& .MuiCollapse-wrapper': {
								display: 'flex',
								flex: 1,
								minHeight: 0,
							},
							'& .MuiCollapse-wrapperInner': {
								display: 'flex',
								flex: 1,
								minHeight: 0,
							},
						}}
					>
						<Fade
							in={!isGridCollapsed}
							timeout={{ enter: 500, exit: 100 }}
							style={{
								transitionDelay: !isGridCollapsed ? '200ms' : '0ms',
								display: 'flex',
								flex: 1,
								minHeight: 0,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									overflow: 'hidden',
									alignSelf: 'stretch',
									flex: 1,
									height: '100%',
									minHeight: 0,
								}}
							>
								<Typography
									variant='h4'
									sx={{
										fontFamily: 'var(--font-family)',
										fontSize: '2.125rem',
										fontWeight: 600,
										lineHeight: '123.5%',
										justifyContent: 'center',
										display: 'flex',
										marginTop: '20px',
										color: 'var(--primary-dark)',
										alignSelf: 'stretch',
									}}
								>
									Gerir Folhas de Ponto
								</Typography>
								<TabPageForApproval />
							</Box>
						</Fade>
					</Collapse>
				</Box>
				<Box
					data-area='folha-de-ponto'
					component={'div'}
					sx={{
						position: 'relative',
						flexGrow: 1,
						overflowY: 'visible',
						padding: 0,
					}}
				>
					<ApprovalDetailsPage />
				</Box>
			</Box>
			<TableMotoristasFiltersService />
		</Box>
	)
}

export default Timesheet
